import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { SkinnyMeetingDefinition } from "src/queries";
import { SkinnyMeeting } from "src/queries/useMeetingSearchResultsNewPage";
import FollowUpTemplateDropdown from "./FollowUpTemplateDropdown";
import { CreateTemplateModal } from "./dialogs";
import { MeetingStatus } from "src/types";

interface TemplateCellProps {
  meeting: SkinnyMeeting;
  onChange: (template: SkinnyMeetingDefinition) => void;
  canChangeTemplate: boolean;
}

const isProvisioningCategory = (status: MeetingStatus): boolean => {
  return (
    status === "initializing" ||
    status === "initialized" ||
    status === "init_fault"
  );
};

export function TemplateCell({
  meeting,
  onChange,
  canChangeTemplate,
}: TemplateCellProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [showCreateTemplateModal, setShowCreateTemplateModal] =
    useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const canEdit = canChangeTemplate && isProvisioningCategory(meeting.status);

  // Handle Escape key and click outside to close editing mode
  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsEditing(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsEditing(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing]);

  if (isEditing) {
    return (
      <Stack
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <FollowUpTemplateDropdown
          value={meeting.meetingType as SkinnyMeetingDefinition}
          onChange={(template: SkinnyMeetingDefinition | null) => {
            if (!template) {
              return;
            }
            onChange(template);
          }}
          onCreateNewTemplate={() => {
            setIsEditing(false);
            setShowCreateTemplateModal(true);
          }}
          refreshTrigger={refreshTrigger}
        />
      </Stack>
    );
  }

  return (
    <>
      <Stack
        onClick={(e) => {
          if (canEdit) {
            e.stopPropagation();
            setIsEditing(true);
          }
        }}
        onMouseDown={(e) => {
          if (canEdit) {
            e.stopPropagation();
          }
        }}
        onKeyDown={(e) => {
          if (canEdit && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            e.stopPropagation();
            setIsEditing(true);
          }
        }}
        role={canEdit ? "button" : undefined}
        tabIndex={canEdit ? 0 : undefined}
        sx={{
          gap: "6px",
          cursor: canEdit ? "pointer" : "default",
          padding: 1,
          borderRadius: canEdit ? 1 : 0,
          minHeight: 40,
          justifyContent: "center",
          "&:hover": canEdit ? { backgroundColor: "action.hover" } : {},
        }}
      >
        <Typography fontSize="14px">{meeting.meetingType.name}</Typography>

        {meeting.attempts?.total > 1 && (
          <Stack sx={{ flexDirection: "row", gap: "4px" }}>
            {new Array(meeting.attempts.current).fill(null).map((_, index) => (
              <Box
                key={`attempt-${meeting.id}-${index}`}
                sx={{
                  borderRadius: 999,
                  height: "12px",
                  width: "12px",
                  backgroundColor: "primary.main",
                }}
              />
            ))}
            {new Array(
              Math.max(meeting.attempts.total - meeting.attempts.current, 0),
            )
              .fill(null)
              .map((_, index) => (
                <Box
                  key={`attempt-${meeting.id}-${index}`}
                  sx={{
                    borderRadius: 999,
                    height: "12px",
                    width: "12px",
                    backgroundColor: "#CDD6DD",
                  }}
                />
              ))}
          </Stack>
        )}
      </Stack>

      <CreateTemplateModal
        open={showCreateTemplateModal}
        onClose={() => {
          setShowCreateTemplateModal(false);
        }}
        onTemplateCreated={(newTemplate) => {
          onChange(newTemplate);
          setShowCreateTemplateModal(false);
          setRefreshTrigger((prev) => prev + 1);
        }}
      />
    </>
  );
}
